import React, { useMemo } from 'react'

import * as Yup from 'yup'
import I18n from 'i18n'
import { t } from 'i18n'
import { useNotify } from 'utils/useNotify'
import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Form, Formik, FormikHelpers } from 'formik'
import { TextField, CheckboxField, PasswordField } from 'components/formik'
import Button from 'components/button'
import { getMetaValue } from 'utils'
import { commonStyles, fetchJson, SignUp, useSignUp } from './common'
import TermsAndConditions from './termsAndConditions'
import { sample, random } from 'lodash'

const useStyles = makeStyles((theme) => ({
  ...commonStyles(theme),
  mdPaddingRight: {
    [theme.breakpoints.up('md')]: {
      paddingRight: theme.spacing(2)
    }
  },
  clearfixSelector: {
    // because naming is visible in DOM
    display: 'none'
  },
  alignmentSelector: {
    // because naming is visible in DOM
    position: 'absolute',
    left: random(-10000, -1000),
    top: random(-10000, -1000)
  }
}))

const schema = Yup.object({
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  email: Yup.string().email().required(),
  password: Yup.string().required().min(9),
  passwordConfirmation: Yup.string().required(),
  acceptTermsAndConditions: Yup.boolean().when('nextButtonClicked', {
    is: true,
    then: Yup.boolean().oneOf([true], t('sign_up.credentials_form.accept_terms_and_conditions_error'))
  }),
  contactMe: Yup.boolean()
})

interface CredentialsFormProps extends SignUp {
  password?: string
  passwordConfirmation?: string
  acceptTermsAndConditions?: boolean
  contactMe?: boolean
  nextButtonClicked?: boolean
}

const initialValues: CredentialsFormProps = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  passwordConfirmation: '',
  acceptTermsAndConditions: false,
  contactMe: false,
  nextButtonClicked: false
}

// Keep in sync with RegistrationsController
const HONEYPOTS = [
  { name: 'city', label: 'City' },
  { name: 'street', label: 'Street name' },
  { name: 'username', label: 'User name' },
  { name: 'title', label: 'Title' }
]

const Honeypot: React.FC = () => {
  const classes = useStyles()

  const { name, label } = useMemo(() => sample(HONEYPOTS) || HONEYPOTS[0], [])
  const className = useMemo(
    () => sample([classes.clearfixSelector, classes.alignmentSelector]),
    [classes.alignmentSelector, classes.clearfixSelector]
  )

  return <TextField name={name} fullWidth label={label} placeholder={label} className={className} autoComplete='off' />
}

export const CredentialsForm: React.FC = () => {
  const classes = useStyles()

  const { signUp, setSignUp } = useSignUp()

  const { notifyError } = useNotify()
  const handleSubmit = async (values: CredentialsFormProps, { setSubmitting }: FormikHelpers<CredentialsFormProps>) => {
    try {
      setSubmitting(true)
      const freshSignUp = await fetchJson<SignUp>(`/${I18n.locale}/sign_up`, {
        method: 'PATCH',
        bodyHash: {
          ...values,
          authenticity_token: getMetaValue('csrf-token')
        },
        headers: {
          'Content-Type': 'application/json'
        }
      })
      setSubmitting(false)
      setSignUp(freshSignUp)
    } catch (error) {
      setSubmitting(false)
      let message: string | undefined
      if (error?.isValidationError) {
        const freshSignUp: SignUp = await error?.response.json()
        setSignUp(freshSignUp)
        message = freshSignUp.errors
      } else {
        message = ''
      }
      notifyError({ message })
    }
  }

  return (
    <Formik initialValues={{ ...initialValues, ...signUp }} onSubmit={handleSubmit} validationSchema={schema}>
      {({ isSubmitting, setFieldValue }) => (
        <Form noValidate>
          <Grid container>
            <Grid item xs={12} sm={12} md={6} className={classes.mdPaddingRight}>
              <TextField
                name='firstName'
                required
                fullWidth
                label={t('activerecord.attributes.sign_up.first_name')}
                placeholder={t('sign_up.credentials_form.first_name_placeholder')}
                autoFocus
                className={classes.input}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                name='lastName'
                required
                fullWidth
                label={t('activerecord.attributes.sign_up.last_name')}
                placeholder={t('sign_up.credentials_form.last_name_placeholder')}
                className={classes.input}
              />
            </Grid>
          </Grid>
          <TextField
            name='email'
            required
            fullWidth
            label={t('activerecord.attributes.sign_up.email')}
            placeholder={t('sign_up.credentials_form.email_placeholder')}
            type='email'
            className={classes.input}
          />
          <PasswordField
            name='password'
            required
            fullWidth
            label={t('activerecord.attributes.user_credential.password')}
            placeholder={t('sign_up.credentials_form.password_placeholder')}
            autoComplete='sign-up-password'
            className={classes.input}
          />
          <PasswordField
            name='passwordConfirmation'
            required
            fullWidth
            label={t('activerecord.attributes.user_credential.password_confirmation')}
            placeholder={t('sign_up.credentials_form.password_confirmation_placeholder')}
            autoComplete='sign-up-password-confirmation'
            className={classes.input}
          />
          <Honeypot />
          <CheckboxField name='contactMe' label={t('activerecord.attributes.sign_up.contact_me')} CheckboxProps={{}} />
          <br />
          <TermsAndConditions setFieldValue={setFieldValue} />
          <div className={classes.buttons}>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              className={classes.submit}
              disabled={isSubmitting}
              size='large'
              onClick={() => setFieldValue('nextButtonClicked', true)}
            >
              {t('sign_up.next')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}
